import Skeleton from "@/components/Skeleton/Skeleton";
import Table from "@/components/Table/Table";
import NoDataPoints from "@/components/visualizations/NoDataPoints/NoDataPoints";
import { useQuery } from "@tanstack/react-query";
import { Tabs } from "antd";
import axios from "axios";
import Widget from "../components/Widget/Widget";
import { useDashboardYear } from "../hooks/useDateRange";
import { formatAmountWithUnit, formatCarbonFootprint } from "../utils/formatters";
import { IGhgCategory } from "./types";

export type GhgUtilityItem = {
  facility_name: string;
  utility_source_label: string;
  unit: string;
  amount: number;
  scope_1: number;
  scope_2: number;
  scope_3: number;
};

export type GhgGoodsAndServicesItem = {
  facility_name: string;
  name: string;
  unit: string;
  amount: number;
  scope_1: number;
  scope_2: number;
  scope_3: number;
};

export type GhgTransportItem = {
  travel_mode: string;
  type: string;
  distance: number;
  mass: number;
  scope_1: number;
  scope_2: number;
  scope_3: number;
};

export type GhgMaterialItem = {
  name: string;
  unit: string;
  quantity: number;
  scope_1: number;
  scope_2: number;
  scope_3: number;
};

export type GhgConsumableItem = {
  name: string;
  unit: string;
  scope_1: number;
  scope_2: number;
  scope_3: number;
};

export type GhgElectricityItem = {
  name: string;
  unit: string;
  scope_1: number;
  scope_2: number;
  scope_3: number;
};

export type GhgEolItem = {
  name: string;
  unit: string;
  quantity: number;
  scope_1: number;
  scope_2: number;
  scope_3: number;
};

export type GhgEmployeeItem = {
  name: string;
  unit: string;
  type: string;
  distance: number;
  scope_1: number;
  scope_2: number;
  scope_3: number;
};

export type GhgConsumableEolItem = {
  name: string;
  unit: string;
  quantity: number;
  scope_1: number;
  scope_2: number;
  scope_3: number;
};

export type IGhgCategoryDetails = {
  utilities: GhgUtilityItem[];
  transport: GhgTransportItem[];
  goods_and_services: GhgGoodsAndServicesItem[];
  materials: GhgMaterialItem[];
  consumables: GhgConsumableItem[];
  electricity: GhgElectricityItem[];
  employee: GhgEmployeeItem[];
  eol: GhgEolItem[];
  consumables_eol: GhgConsumableEolItem[];
  external: {
    data: any[];
    columns: string[];
  };
};

export default function GhgCategoryDetails({ category }: { category: IGhgCategory }) {
  const { year } = useDashboardYear();

  const mainScope = category.startsWith("scope_1")
    ? "scope_1"
    : category.startsWith("scope_2")
      ? "scope_2"
      : "scope_3";

  const { data, isLoading } = useQuery({
    queryKey: ["dashboard", "ghg", "category_details", category, year],
    enabled: !!year,
    queryFn: () =>
      axios
        .get<IGhgCategoryDetails>("/ghg/category_details", {
          params: {
            category,
            year,
          },
        })
        .then((result) => result.data),
  });

  let items = [];

  if (data?.transport && data.transport.length > 0) {
    items.push({
      label: "Transport",
      key: "transport",
      children: (
        <Widget title="Transport">
          <Table
            data={data?.transport}
            type="grey"
            loading={isLoading}
            pagination={{ pageSize: 5, page: 1 }}
            columns={[
              {
                label: "Type",
                key: "type",
                renderer: (record) => (record.type === "InboundTransport" ? "Inbound" : "Outbound"),
              },
              {
                label: "Mode",
                key: "travel_mode",
              },
              {
                label: "Distance",
                key: "distance",
                renderer: (record) => formatAmountWithUnit(record.distance, "km"),
              },
              {
                label: "Mass",
                key: "mass",
                renderer: (record) => formatAmountWithUnit(record.mass, "kg"),
              },
              {
                label: mainScope.replace("_", " "),
                key: mainScope,
                renderer: (record) => formatCarbonFootprint(record[mainScope]),
              },
            ]}
            rowKey={""}
          />
        </Widget>
      ),
    });
  }

  if (data?.materials && data.materials.length > 0) {
    items.push({
      label: "Materials",
      key: "materials",
      children: (
        <Widget title={"Materials"}>
          <Table
            type="grey"
            data={data?.materials}
            loading={isLoading}
            pagination={{ pageSize: 5, page: 1 }}
            columns={[
              {
                label: "Material",
                key: "name",
              },
              {
                label: "Amount",
                key: "unit",
                renderer: (record) => formatAmountWithUnit(record.quantity, record.unit),
              },
              {
                label: mainScope.replace("_", " "),
                key: mainScope,
                renderer: (record) => formatCarbonFootprint(record[mainScope]),
              },
            ]}
            rowKey={""}
          />
        </Widget>
      ),
    });
  }

  if (data?.utilities && data.utilities.length > 0) {
    items.push({
      label: "Utilities & consumables",
      key: "utilities",
      children: (
        <Widget title="Utilities">
          <Table
            data={data?.utilities}
            type="grey"
            pagination={{ pageSize: 5, page: 1 }}
            loading={isLoading}
            columns={[
              {
                label: "Source",
                key: "utility_source_label",
              },
              {
                label: "Type",
                key: "type",
              },
              {
                label: "Facility",
                key: "facility_name",
              },
              {
                label: "Amount",
                key: "unit",
                renderer: (record) => formatAmountWithUnit(record.amount, record.unit),
              },
              {
                label: mainScope.replace("_", " "),
                key: mainScope,
                renderer: (record) => formatCarbonFootprint(record[mainScope]),
              },
            ]}
            rowKey={""}
          />
        </Widget>
      ),
    });
  }

  if (data?.goods_and_services && data.goods_and_services.length > 0) {
    items.push({
      label: "Goods & services",
      key: "goods_and_services",
      children: (
        <Widget title="Goods & services">
          <Table
            data={data?.goods_and_services}
            type="grey"
            pagination={{ pageSize: 5, page: 1 }}
            loading={isLoading}
            columns={[
              {
                label: "Name",
                key: "name",
              },
              {
                label: "Facility",
                key: "facility_name",
              },
              {
                label: "Amount",
                key: "unit",
                renderer: (record) => formatAmountWithUnit(record.amount, record.unit),
              },
              {
                label: mainScope.replace("_", " "),
                key: mainScope,
                renderer: (record) => formatCarbonFootprint(record[mainScope]),
              },
            ]}
            rowKey={"name"}
          />
        </Widget>
      ),
    });
  }

  if (data?.electricity && data.electricity.length > 0) {
    items.push({
      label: "Usage - electricity",
      key: "usage_electricity",
      children: (
        <Widget title="Usage - electricity">
          <Table
            data={data?.electricity}
            type="grey"
            loading={isLoading}
            pagination={{ pageSize: 5, page: 1 }}
            columns={[
              {
                label: "Name",
                key: "name",
              },
              {
                label: "Unit",
                key: "unit",
              },
              {
                label: mainScope.replace("_", " "),
                key: mainScope,
                renderer: (record) => formatCarbonFootprint(record[mainScope]),
              },
            ]}
            rowKey={""}
          />
        </Widget>
      ),
    });
  }

  if (data?.consumables && data.consumables.length > 0) {
    items.push({
      label: "Usage - consumables",
      key: "usage_consumables",
      children: (
        <Widget title="Usage - consumables">
          <Table
            data={data?.consumables}
            type="grey"
            loading={isLoading}
            pagination={{ pageSize: 5, page: 1 }}
            columns={[
              {
                label: "Name",
                key: "name",
              },
              {
                label: "Unit",
                key: "unit",
              },
              {
                label: mainScope.replace("_", " "),
                key: mainScope,
                renderer: (record) => formatCarbonFootprint(record[mainScope]),
              },
            ]}
            rowKey={""}
          />
        </Widget>
      ),
    });
  }

  if (data?.eol && data.eol.length > 0) {
    items.push({
      label: "End of life",
      key: "eol",
      children: (
        <Widget title="End of life">
          <Table
            data={data?.eol}
            type="grey"
            loading={isLoading}
            pagination={{ pageSize: 5, page: 1 }}
            columns={[
              {
                label: "Name",
                key: "name",
              },
              {
                label: "Quantity",
                key: "quantity",
                renderer: (record) => formatAmountWithUnit(record.quantity, record.unit),
              },
              {
                label: mainScope.replace("_", " "),
                key: mainScope,
                renderer: (record) => formatCarbonFootprint(record[mainScope]),
              },
            ]}
            rowKey={""}
          />
        </Widget>
      ),
    });
  }

  if (data?.employee && data.employee.length > 0) {
    items.push({
      label: "Employees",
      key: "employees",
      children: (
        <Widget title="Employees">
          <Table
            data={data?.employee}
            type="grey"
            loading={isLoading}
            pagination={{ pageSize: 5, page: 1 }}
            columns={[
              {
                label: "Name",
                key: "name",
              },
              {
                label: "Type",
                key: "type",
                renderer: (record) =>
                  record.type === "business"
                    ? "Business travel"
                    : record.type === "commute"
                      ? "Commute"
                      : "-",
              },
              {
                label: "Distance",
                key: "distance",
                renderer: (record) => formatAmountWithUnit(record.distance, record.unit),
              },
              {
                label: mainScope.replace("_", " "),
                key: mainScope,
                renderer: (record) => formatCarbonFootprint(record[mainScope]),
              },
            ]}
            rowKey={""}
          />
        </Widget>
      ),
    });
  }

  if (data?.consumables_eol && data.consumables_eol.length > 0) {
    items.push({
      label: "Consumables - end of life",
      key: "consumables_eol",
      children: (
        <Widget title="Consumables - end of life">
          <Table
            data={data?.consumables_eol}
            type="grey"
            loading={isLoading}
            pagination={{ pageSize: 5, page: 1 }}
            columns={[
              {
                label: "Name",
                key: "name",
              },
              {
                label: "Quantity",
                key: "quantity",
                renderer: (record) => formatAmountWithUnit(record.quantity, record.unit),
              },
              {
                label: mainScope.replace("_", " "),
                key: mainScope,
                renderer: (record) => formatCarbonFootprint(record[mainScope]),
              },
            ]}
            rowKey={""}
          />
        </Widget>
      ),
    });
  }

  if (data?.external && data.external.data.length > 0) {
    items.push({
      label: "External",
      key: "external",
      children: (
        <Widget title="External">
          <Table
            data={data?.external.data}
            type="grey"
            loading={isLoading}
            pagination={{ pageSize: 5, page: 1 }}
            columns={[
              {
                label: "Name",
                key: "name",
                minWidth: "250px",
              },
              {
                label: "Quantity",
                key: "quantity",
                renderer: (record) => formatAmountWithUnit(record.quantity, record.unit),
                minWidth: "250px",
              },
              {
                label: mainScope.replace("_", " "),
                key: "emissions",
                renderer: (record) => formatCarbonFootprint(record.emissions),
                minWidth: "250px",
              },
              ...data?.external.columns.map((x) => ({ label: x, key: x, minWidth: "250px" })),
            ]}
            rowKey={""}
          />
        </Widget>
      ),
    });
  }

  if (isLoading) {
    items = [
      {
        label: <Skeleton style={{ width: 80, height: 22 }} />,
        key: "loading1",
        children: <Skeleton style={{ height: 20 }} />,
      },
      {
        label: <Skeleton style={{ width: 60, height: 22 }} />,
        key: "loading2",
        children: <Skeleton style={{ height: 20 }} />,
      },
    ];
  }

  if (items.length === 0) return <NoDataPoints />;

  return <Tabs items={items} />;
}
